import { useMemo } from 'react';
import dayjs from 'dayjs';
import { OMS_COLORS } from '../omsDashTheme';
import type { OmsEngineerActivityDay } from '../../../../services/dashboardService';
import type { ScopeWindow } from '../utils/scopeWindow';

interface Props {
  days: OmsEngineerActivityDay[];
  window: ScopeWindow;
  name: string;
}

/** Render cap so a multi-year custom window stays readable. */
const MAX_DAYS = 26 * 7;

const WEEKDAY_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', ''];

/** Mint intensity ladder — engraved, not glowing. */
function cellColor(total: number, max: number): string {
  if (total === 0) return 'rgba(11,59,46,0.06)';
  const step = Math.min(3, Math.ceil((total / Math.max(max, 1)) * 3));
  return ['#BFDCCF', '#7BBFA3', '#339274', '#1F5A47'][step] ?? '#339274';
}

/**
 * Contribution-style calendar of an engineer's submissions across the
 * selected window. Never colour-only: every cell carries a tooltip and an
 * aria-label with exact pass/fail counts, and the header states the totals.
 */
export default function ActivityHeatmap({ days, window, name }: Props) {
  const { weeks, monthLabels, max, total, clipped } = useMemo(() => {
    const byDate = new Map(days.map((d) => [d.date, d]));
    let start = dayjs(window.from);
    const end = dayjs(window.to);
    const spanDays = end.diff(start, 'day') + 1;
    const isClipped = spanDays > MAX_DAYS;
    if (isClipped) start = end.subtract(MAX_DAYS - 1, 'day');
    // Align the grid to the Monday of the first week.
    const gridStart = start.subtract((start.day() + 6) % 7, 'day');
    const weekCount = Math.ceil((end.diff(gridStart, 'day') + 1) / 7);

    const grid: Array<Array<{ date: string; inWindow: boolean; entry: OmsEngineerActivityDay | null }>> = [];
    const labels: Array<{ week: number; label: string }> = [];
    let lastMonth = '';
    for (let w = 0; w < weekCount; w++) {
      const column: (typeof grid)[number] = [];
      for (let d = 0; d < 7; d++) {
        const date = gridStart.add(w * 7 + d, 'day');
        const key = date.format('YYYY-MM-DD');
        const inWindow = !date.isBefore(start, 'day') && !date.isAfter(end, 'day');
        column.push({ date: key, inWindow, entry: inWindow ? (byDate.get(key) ?? null) : null });
        if (d === 0) {
          const month = date.format('MMM');
          if (month !== lastMonth) {
            labels.push({ week: w, label: month });
            lastMonth = month;
          }
        }
      }
      grid.push(column);
    }
    const visibleTotals = days.filter((d) => !dayjs(d.date).isBefore(start, 'day'));
    return {
      weeks: grid,
      monthLabels: labels,
      max: Math.max(...visibleTotals.map((d) => d.total), 0),
      total: visibleTotals.reduce((s, d) => s + d.total, 0),
      clipped: isClipped,
    };
  }, [days, window]);

  const CELL = 13;
  const GAP = 3;

  return (
    <div
      role="img"
      aria-label={`Submission activity for ${name}: ${total} submissions across the selected window.`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[11px] font-semibold" style={{ color: OMS_COLORS.caption }}>
          <span className="font-extrabold tabular-nums" style={{ color: OMS_COLORS.tealDark }}>
            {total}
          </span>{' '}
          submission{total === 1 ? '' : 's'} in this window
          {clipped && ' (showing the most recent 26 weeks)'}
        </p>
        <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide" style={{ color: OMS_COLORS.caption }}>
          Less
          {[0, 1, 2, 3].map((step) => (
            <span
              key={step}
              className="h-2.5 w-2.5 rounded-[3px]"
              style={{ backgroundColor: step === 0 ? 'rgba(11,59,46,0.06)' : ['#BFDCCF', '#7BBFA3', '#1F5A47'][step - 1] }}
            />
          ))}
          More
        </span>
      </div>

      <div className="sovereign-scrollbar mt-2 overflow-x-auto pb-1">
        <div className="inline-block">
          {/* Month labels */}
          <div className="relative mb-1 h-3.5" style={{ marginLeft: 30 }}>
            {monthLabels.map((m) => (
              <span
                key={`${m.label}-${m.week}`}
                className="absolute text-[9px] font-bold uppercase tracking-wide"
                style={{ left: m.week * (CELL + GAP), color: OMS_COLORS.caption }}
              >
                {m.label}
              </span>
            ))}
          </div>
          <div className="flex gap-[3px]">
            {/* Weekday gutter */}
            <div className="flex w-[27px] flex-col gap-[3px] pr-1">
              {WEEKDAY_LABELS.map((label, i) => (
                <span key={i} className="flex items-center text-[8px] font-bold uppercase" style={{ height: CELL, color: OMS_COLORS.caption }}>
                  {label}
                </span>
              ))}
            </div>
            {weeks.map((column, w) => (
              <div key={w} className="flex flex-col gap-[3px]">
                {column.map((cell) => {
                  if (!cell.inWindow) {
                    return <span key={cell.date} style={{ width: CELL, height: CELL }} aria-hidden />;
                  }
                  const entry = cell.entry;
                  const totalOfDay = entry?.total ?? 0;
                  const text = entry
                    ? `${dayjs(cell.date).format('D MMM YYYY')} — ${entry.total} submission${entry.total === 1 ? '' : 's'} (${entry.pass} pass · ${entry.fail} fail)`
                    : `${dayjs(cell.date).format('D MMM YYYY')} — no submissions`;
                  return (
                    <span
                      key={cell.date}
                      title={text}
                      aria-label={text}
                      className="rounded-[3px]"
                      style={{ width: CELL, height: CELL, backgroundColor: cellColor(totalOfDay, max) }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
